import React, { useEffect, useState } from 'react';
import { Button, Col, Row, Select, Table, Typography, message, Input, Tooltip } from 'antd';
import { CameraOutlined, StopOutlined, CheckCircleTwoTone, CheckOutlined, EyeOutlined } from '@ant-design/icons';
import axios from '../../config/axiosInstance';
import QRCodeScanner from './QRCodeScanner';
import { useTheme } from "../../context/ThemeContext";
import useControlListData from './useControlListData';
import ParticipantDetailsModal from './ParticipantDetailsModal';

const { Text } = Typography;
const { Title } = Typography;
const { Option } = Select;
const { Search } = Input;

const ControlList = () => {
    const { darkTheme } = useTheme();
    const [scannerVisible, setScannerVisible] = useState(false);
    const [eventId, setEventId] = useState(null);
    const [selectedProgrammePointId, setSelectedProgrammePointId] = useState(null);
    const [selectedHost, setSelectedHost] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [recentScans, setRecentScans] = useState({});
    const [pageSize, setPageSize] = useState(25);
    const [selectedDeviceConfigId, setSelectedDeviceConfigId] = useState(null);
    const [detailsModalData, setDetailsModalData] = useState(null);

    const {
        participants,
        checkedIds,
        setCheckedIds,
        fetchParticipantsAndLeads,
        deviceConfigs,
        fetchDeviceConfigs,
        fetchParticipantDetails,
    } = useControlListData();

    useEffect(() => {
        fetchDeviceConfigs();
    }, [fetchDeviceConfigs]);

    useEffect(() => {
        if (!eventId || !selectedProgrammePointId || !selectedHost) return;

        fetchParticipantsAndLeads(eventId, selectedProgrammePointId, selectedHost);

        const interval = setInterval(() => {
            fetchParticipantsAndLeads(eventId, selectedProgrammePointId, selectedHost);
        }, 3000);

        return () => clearInterval(interval);
    }, [eventId, selectedProgrammePointId, selectedHost, fetchParticipantsAndLeads]);

    const filteredParticipants = participants.filter(p =>
        p.vigenereCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
        `${p.firstName} ${p.lastName}`.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleScan = async (qr) => {
        const now = Date.now();
        if (recentScans[qr] && now - recentScans[qr] < 20000) return;
        setRecentScans(prev => ({ ...prev, [qr]: now }));

        const match = participants.find(p => p.vigenereCode === qr);
        if (match) {
            const success = await createLead(match.key, qr);
            if (success) setCheckedIds(prev => prev.includes(qr) ? prev : [...prev, qr]);
        } else {
            message.error("Participant not found");
            setRecentScans(prev => {
                const copy = { ...prev };
                delete copy[qr];
                return copy;
            });
        }
    };

    const manuallyCheck = async (code) => {
        const match = participants.find(p => p.vigenereCode === code);
        if (match) {
            const success = await createLead(match.key, code);
            if (success) setCheckedIds(prev => prev.includes(code) ? prev : [...prev, code]);
        }
    };

    const createLead = async (participantId, barcode) => {
        if (!eventId || !selectedProgrammePointId || !selectedHost) return false;

        try {
            const res = await axios.get(`/regasus/token?host=${selectedHost}`);
            const token = res.data?.access_token;
            if (!token) return false;

            const response = await axios.post('/regasus/create-lead', {
                participantId,
                programmePointID: selectedProgrammePointId,
                direction: 'I',
            }, {
                params: {
                    host: selectedHost,
                    token,
                    barcode,
                },
            });

            if (response.status >= 200 && response.status < 300) {
                message.success('✔️ Lead Created');
                return true;
            } else {
                console.warn('⚠️ Lead creation responded with error status:', response.status);
                message.error('Error creating lead');
                return false;
            }

        } catch (error) {
            console.error('❌ Error creating lead:', error.response || error.message);
            message.error('Error creating lead');
            return false;
        }
    };

    const columns = [
        {
            title: "Checked",
            key: "checked",
            align: 'center',
            render: (_, row) => checkedIds.includes(row.vigenereCode) ? (
                <CheckCircleTwoTone twoToneColor="#52c41a" />
            ) : (
                <Tooltip title="Mark as checked">
                    <Button icon={<CheckOutlined />} size="small" onClick={() => manuallyCheck(row.vigenereCode)} />
                </Tooltip>
            )
        },
        {
            title: "Name",
            dataIndex: "firstName",
            key: "firstName",
            render: (_, row) => `${row.firstName} ${row.lastName}`
        },
        { title: "Organisation", dataIndex: "organisation", key: "organisation" },
    ];

    // Only adds the column if the selected configuration has CustomFields
    const selectedConfig = deviceConfigs.find(cfg => cfg._id === selectedDeviceConfigId);
    if (selectedConfig?.CustomFields?.length > 0) {
        columns.push({
            title: "Custom Fields",
            key: "CustomFields",
            align: "center",
            render: (_, row) => (
                <Tooltip title="View Details">
                    <Button
                        icon={<EyeOutlined style={{ color: 'skyblue' }}/>}
                        onClick={async () => {
                            const selectedConfig = deviceConfigs.find(cfg => cfg._id === selectedDeviceConfigId);
                            if (!selectedConfig) return;

                            const host = selectedConfig.SelectedEventId?.host_url;
                            const participantId = row.key;

                            const details = await fetchParticipantDetails({ host, participantId });
                            if (details) {
                                setDetailsModalData({
                                    participant: details,
                                    config: selectedConfig
                                });
                            }
                        }}
                    />
                </Tooltip>
            )
        });
    }

    return (
        <div style={{ padding: 24 }}>
            <Title level={3}>Control List</Title>
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col span={1}>
                    <Tooltip title={scannerVisible ? 'Stop Scanner' : 'Start Scanner'}>
                        <Button
                            type="primary"
                            danger={scannerVisible}
                            shape="circle"
                            icon={scannerVisible ? <StopOutlined /> : <CameraOutlined />}
                            onClick={() => setScannerVisible(prev => !prev)}
                        />
                    </Tooltip>
                </Col>
                <Col xs={24} sm={12}>
                    <Select
                        placeholder="Select a Configuration"
                        onChange={(value) => {
                            setSelectedDeviceConfigId(value);

                            const selectedConfig = deviceConfigs.find(cfg => cfg._id === value);
                            if (!selectedConfig) return;

                            const event = selectedConfig.SelectedEventId;
                            const programmePointId = selectedConfig.SelectedProgrammePointId;
                            const host = event?.host_url;
                            const eventId = event?.eventId;

                            if (eventId && host) {
                                setEventId(eventId);
                                setSelectedProgrammePointId(programmePointId || null);
                                setSelectedHost(host);
                                fetchParticipantsAndLeads(eventId, programmePointId, host);
                            } else {
                                message.warning('Missing eventId or host_url in selected DeviceConfig');
                            }
                        }}
                        style={{ width: '100%' }}
                        value={selectedDeviceConfigId || undefined}
                    >
                        {deviceConfigs.map((config) => (
                            <Option key={config._id} value={config._id}>
                                {config.DeviceConfigName}
                            </Option>
                        ))}
                    </Select>
                </Col>
                <Col xs={24} sm={11}>
                    <div style={{ display: 'flex', justifyContent: 'right', alignItems: 'center', height: '100%' }}>
                        <Typography.Text strong>
                            {(() => {
                                const selectedConfig = deviceConfigs.find(cfg => cfg._id === selectedDeviceConfigId);
                                const mnemonic = selectedConfig?.SelectedEventId?.mnemonic || 'No Event';
                                const ppName = selectedConfig?.SelectedProgrammePointName || 'No Programme Point';
                                return `${mnemonic} - ${ppName}`;
                            })()}
                        </Typography.Text>
                    </div>
                </Col>
            </Row>

            <Row gutter={24}>
                <Col xs={24} sm={scannerVisible ? 10 : 0} style={{ transition: 'all 0.3s ease', marginBottom: 16 }}>
                    {scannerVisible && <QRCodeScanner onScan={handleScan} />}
                </Col>

                <Col xs={24} sm={scannerVisible ? 14 : 24}>
                    <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                        <Col xs={24} sm={13}>
                            <Search
                                placeholder="Search by name or code"
                                allowClear
                                enterButton
                                onSearch={(value) => setSearchTerm(value)}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{ width: '100%' }}
                            />
                        </Col>
                        <Col xs={24} sm={8} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                            <Text type="secondary" style={{ whiteSpace: 'nowrap' }}>
                                <span style={{ color: darkTheme ? '#40a9ff' : '#096dd9' }}>
                                    <strong>{filteredParticipants.length}</strong> participants found
                                </span>
                                {" • "}
                                <span style={{ color: darkTheme ? '#52c41a' : '#389e0d' }}>
                                    <strong>{filteredParticipants.filter(p => checkedIds.includes(p.vigenereCode)).length}</strong> checked
                                </span>
                            </Text>
                        </Col>
                    </Row>

                    <Table
                        rowKey="vigenereCode"
                        scroll={{ x: 'max-content' }}
                        dataSource={filteredParticipants}
                        columns={columns}
                        pagination={{
                            pageSize,
                            showSizeChanger: true,
                            pageSizeOptions: ['25', '50', '100', filteredParticipants.length.toString()],
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                            onShowSizeChange: (_, size) => setPageSize(size),
                        }}
                        size="small"
                        bordered
                        rowClassName={(record) => checkedIds.includes(record.vigenereCode) ? 'checked-row' : ''}
                    />
                </Col>
            </Row>

            <style>{`
                .checked-row {
                    background-color: ${darkTheme ? '#173d2e' : '#f6ffed'} !important;
                }
            `}</style>

            <ParticipantDetailsModal
                open={!!detailsModalData}
                onClose={() => setDetailsModalData(null)}
                participantData={detailsModalData}
                host={selectedHost}
            />

        </div>
    );
};

export default ControlList;